import { Sheet } from 'framework7-react';
import { useEffect, useState } from 'react';

type Props = Omit<
  React.ComponentProps<typeof Sheet>,
  'opened' | 'push' | 'swipeToClose' | 'backdrop'
> & {
  open: boolean;
  onClose: () => void;
  /**
   * Enable swipe-down-to-close (touch only). OFF by default: F7's swipe touch
   * handler has historically thrown "Cannot read properties of undefined
   * (reading 'swipeToClose')" when it fires against a torn-down sheet, so opt in
   * only for sheets that stay mounted for the session (e.g. the app-shell
   * AssistantSheet). We additionally gate the flag on `opened` below, so the handler
   * is bound ONLY while the sheet is actually open — never during/after teardown.
   */
  swipeToClose?: boolean;
};

/**
 * Wraps F7 Sheet with a backdrop, and defers the `opened` prop by one animation
 * frame so React effects that run on the same render as the open-state change
 * don't interrupt F7's opening animation.
 *
 * The old iOS `push` (page-zoom-out) effect was removed — the shrinking-app
 * animation behind the sheet was distracting; the sheet now just slides up over
 * a backdrop with the app held still.
 */
export default function PushSheet({
  open,
  onClose,
  swipeToClose = false,
  children,
  ...props
}: Props) {
  const [opened, setOpened] = useState(false);

  useEffect(() => {
    if (!open) {
      setOpened(false);
      return;
    }
    const id = requestAnimationFrame(() => setOpened(true));
    return () => cancelAnimationFrame(id);
  }, [open]);

  return (
    <Sheet
      opened={opened}
      onSheetClosed={onClose}
      backdrop
      // Bind the swipe handler only while open, so it can't fire against a
      // torn-down instance once the sheet closes.
      swipeToClose={swipeToClose && opened}
      {...props}
    >
      {children}
    </Sheet>
  );
}
